import { ChangeEvent, FC, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Box, Divider, Stack, Typography } from "@mui/material";

import { PaypalButton } from "components/Payment/shared/components";
import { FormContainer } from "components/shared";
import { useAppData, useUserStore } from "providers/RootStoreProvider";
import { isProductionHost } from "helpers/isProductionHost";
import { PAYMENT_PLAN_TYPE } from "constants/env.const";
import { VATCountries } from "constants/VATCountries.const";
import { themeColors } from "constants/colors.const";
import { PaymentPlanType } from "enums/payment.enum";
import { parseTrialDurationDays } from "helpers/dateTime";

import { DisclaimersFormBottomBlock } from "./components";
import { PaywallType } from "../../shared/utils";
import { IxopayFormProps } from "../../shared/types";
import { IxopayForm } from "components/Payment/shared/IxopayForm";
import { themeFonts } from "mui-theme/themeFonts.const";

const PaymentForm: FC<IxopayFormProps> = observer(({ data, paypalPlan, formatLocaleCurrency }) => {
    const { t } = useTranslation();
    const { flowOuter, amplitudeUserId, abTestData } = useAppData();
    const { userWebSession } = useUserStore();
    const userData = userWebSession?.session?.user;
    const [checked, setChecked] = useState(flowOuter || false);

    const hasFormData = Boolean(data);
    const { showPaymentPlans, plan } = data || {};
    const priceValue = PAYMENT_PLAN_TYPE === PaymentPlanType.SUBSCRIPTION_WITH_TRIAL ? plan?.trialPrice : plan?.price;
    const isUsUser = userData?.countryCode === "US";
    const isUSAflowOuter = flowOuter && isUsUser;
    const isVATUser = VATCountries.some((country) => country.code === userData?.countryCode);
    const formattedPrice = formatLocaleCurrency(priceValue);
    const formattedRenewalPrice = formatLocaleCurrency(plan?.price);
    const trialDurationDays = parseTrialDurationDays(plan?.trialDuration);
    const trialLabel = trialDurationDays === 1 ? t("24h Trial") : t("trial_days_label", { days: trialDurationDays });

    const abTestPaymentScreenOptionsLegal = userWebSession?.session?.abTestData?.abTestPaymentScreenOptionsLegal || abTestData?.abTestPaymentScreenOptionsLegal;
    const abTestPaymentScreenOptionsNonLegal =
        userWebSession?.session?.abTestData?.abTestPaymentScreenOptionsNonLegal || abTestData?.abTestPaymentScreenOptionsNonLegal;

    const paymentScreenOptionsLegal = JSON.parse(abTestPaymentScreenOptionsLegal);
    const paymentScreenOptionsNonLegal = JSON.parse(abTestPaymentScreenOptionsNonLegal);

    const isActivePaymentMethod = (provider: "ixopay" | "paypal") => {
        const providerActivationCode = provider === "paypal" ? 1 : 2;
        return (
            (!flowOuter &&
                (paymentScreenOptionsLegal[userData?.countryCode] === providerActivationCode || paymentScreenOptionsLegal[userData?.countryCode] === 3)) ||
            (flowOuter &&
                (paymentScreenOptionsNonLegal[userData?.countryCode] === providerActivationCode || paymentScreenOptionsNonLegal[userData?.countryCode] === 3))
        );
    };
    const isShowPaypal = isActivePaymentMethod("paypal");
    // TODO remove first half of condition after bank approvement
    const showIxopayForm = (!isProductionHost() && !flowOuter) || isActivePaymentMethod("ixopay");

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
    };

    if (!hasFormData) return null;

    return (
        <FormContainer
            sx={{
                position: "relative",
                padding: flowOuter && isShowPaypal && !showIxopayForm ? "1.5rem 0" : undefined,
            }}
        >
            <Stack
                mb={0}
                sx={
                    flowOuter && isShowPaypal && !showIxopayForm
                        ? {
                              padding: "0 1.5rem",
                              marginBottom: "3rem",
                              maxHeight: flowOuter ? "322px" : "unset",
                              overflowY: flowOuter ? "auto" : "unset",
                              "-webkit-overflow-scrolling": "touch",
                              "&::-webkit-scrollbar": {
                                  width: "8px",
                                  transform: "translateX(30px)",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                  backgroundColor: "#888",
                                  borderRadius: "5px",
                              },
                              "&::-webkit-scrollbar-track": {
                                  backgroundColor: "transparent",
                                  borderRadius: "5px",
                                  height: "80px",
                              },
                          }
                        : {}
                }
            >
                <Typography variant="h3" mb="1.5rem" align="center" width="100%">
                    {t("Trial subscription")}
                </Typography>
                <Stack direction="row" justifyContent="space-between" mb="1rem">
                    <Typography variant="body1CaptionBold" color={themeColors.brandColors.brandGreen}>
                        {trialLabel}
                    </Typography>
                    <Typography variant="body1CaptionBold" color={themeColors.text.tertiary}>
                        {formattedPrice}
                    </Typography>
                </Stack>
                <Divider sx={{ borderColor: themeColors.brandColors.brandLightgrey, mb: "1rem" }} />
                <Stack direction="row" justifyContent="space-between" mb="1rem">
                    <Typography variant="body1CaptionBold">{t("Then billed at")}:</Typography>
                    <Typography variant="body1CaptionBold" color={themeColors.text.tertiary}>
                        {formattedRenewalPrice}
                    </Typography>
                </Stack>
                {isShowPaypal && (
                    <>
                        <Box
                            sx={
                                !flowOuter && !checked
                                    ? {
                                          width: "100%",
                                          position: "relative",
                                          height: "50px",
                                          "&:before": {
                                              content: '""',
                                              position: "absolute",
                                              inset: 0,
                                              zIndex: 7,
                                              background: themeColors.background.whiteOpacity7,
                                              borderRadius: ".25rem",
                                          },
                                      }
                                    : {
                                          width: "100%",
                                          height: isUSAflowOuter ? "0" : "50px",
                                      }
                            }
                        >
                            <PaypalButton userWebSession={userWebSession} plan={paypalPlan || {}} />
                        </Box>
                        <Box
                            sx={{
                                position: "relative",
                                zIndex: 2,
                            }}
                        >
                            {isShowPaypal && showIxopayForm && (
                                <Divider
                                    variant="fullWidth"
                                    sx={{
                                        margin: "1.5rem auto",
                                        fontSize: "12px",
                                        fontWeight: "400",
                                        "&.MuiDivider-root::before": {
                                            borderTop: "1px solid #383838",
                                        },
                                        "&.MuiDivider-root::after": {
                                            borderTop: "1px solid #383838",
                                        },
                                    }}
                                >
                                    {t("or pay by a card")}
                                </Divider>
                            )}
                        </Box>
                    </>
                )}
                {showIxopayForm && (
                    <Stack direction={"column"} alignItems="center" flex={1} spacing="24px">
                        {amplitudeUserId && data && (
                            <Stack direction="column" flex={1} spacing="15px">
                                <Box className="box--empty" />
                                {showPaymentPlans && (
                                    <Typography variant="h3" alignSelf="center" align="center" maxWidth="260px">
                                        {plan?.name}
                                    </Typography>
                                )}
                                <IxopayForm
                                    data={data}
                                    disableSubmit={!checked}
                                    paywall={PaywallType.default}
                                    sx={{
                                        "& .ixopay-input-iframe": {
                                            height: "56px",
                                        },
                                        "& .ixopay-submit-button": {
                                            width: "100%",
                                            height: "56px",
                                            px: "unset",
                                            textTransform: "uppercase",
                                            backgroundColor: "brandColors.brandGreen",
                                            color: "text.secondary",
                                            fontWeight: 800,
                                            fontSize: "24px",
                                            fontFamily: themeFonts.sourceCodePro,
                                            "&:disabled": {
                                                backgroundColor: "rgba(88, 178, 135, 0.3)",
                                            },
                                        },
                                    }}
                                />
                            </Stack>
                        )}

                        <Box className="ixopay-3ds-flow" sx={{ marginTop: "0px !important" }} />
                    </Stack>
                )}

                <DisclaimersFormBottomBlock
                    onChange={handleChange}
                    checked={checked}
                    plan={plan}
                    ixopayFormShown={showIxopayForm}
                    isVATUser={isVATUser}
                    isUSAUser={isUsUser}
                    formatLocaleCurrency={formatLocaleCurrency}
                />
            </Stack>
        </FormContainer>
    );
});

export default PaymentForm;
